import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { classGroupService } from "../services/class-group.service";
import type {
	CreateClassGroupRequest,
	ListClassGroupsParams,
	UpdateClassGroupRequest,
} from "../types";

// ─── Query Keys ─────────────────────────────────────────────────────
export const classGroupKeys = {
	all: ["class-groups"] as const,
	lists: () => [...classGroupKeys.all, "list"] as const,
	list: (params: ListClassGroupsParams) => [...classGroupKeys.lists(), params] as const,
	details: () => [...classGroupKeys.all, "detail"] as const,
	detail: (id: string) => [...classGroupKeys.details(), id] as const,
};

// ─── useClassGroups (list) ─────────────────────────────────────────────
export function useClassGroups(params: ListClassGroupsParams) {
	return useQuery({
		queryKey: classGroupKeys.list(params),
		queryFn: () => classGroupService.list(params),
		enabled: !!params.organizationId,
	});
}

// ─── useClassGroup (getById) ──────────────────────────────────────────
export function useClassGroup(id: string) {
	return useQuery({
		queryKey: classGroupKeys.detail(id),
		queryFn: () => classGroupService.getById(id),
		enabled: !!id,
	});
}

// ─── useCreateClassGroup ───────────────────────────────────────────────
export function useCreateClassGroup() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateClassGroupRequest) => classGroupService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: classGroupKeys.lists() });
			message.success("Sinf muvaffaqiyatli yaratildi");
		},
	});
}

// ─── useUpdateClassGroup ───────────────────────────────────────────────
export function useUpdateClassGroup() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateClassGroupRequest }) =>
			classGroupService.update(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: classGroupKeys.lists() });
			queryClient.invalidateQueries({ queryKey: classGroupKeys.detail(variables.id) });
			message.success("Sinf muvaffaqiyatli yangilandi");
		},
	});
}

// ─── useDeleteClassGroup ───────────────────────────────────────────────
export function useDeleteClassGroup() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => classGroupService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: classGroupKeys.lists() });
			message.success("Sinf muvaffaqiyatli o'chirildi");
		},
	});
}
